import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

type EnvironmentContextType = {
  apiBaseUrl: string;
  reportsApiUrl: string;
  chatPublicKey?: string;
  environment: string;
  client: 'portal' | 'admin';
  enablePBJ?: boolean;
  azureSSOConfig?: {
    NX_APP_AZURE_AD_CLIENT_ID: string;
    NX_APP_AZURE_AD_AUTHORITY: string;
    NX_APP_AZURE_AD_API_SCOPE: string;
  };
};

const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined
);

type EnvironmentProviderProps = PropsWithChildren<EnvironmentContextType>;

const EnvironmentProvider = ({
  children,
  apiBaseUrl,
  reportsApiUrl = '',
  chatPublicKey,
  environment,
  client,
  azureSSOConfig,
  enablePBJ,
}: EnvironmentProviderProps) => {
  const context = useMemo(
    () => ({
      apiBaseUrl,
      reportsApiUrl,
      chatPublicKey,
      environment,
      client,
      azureSSOConfig,
      enablePBJ,
    }),
    [
      azureSSOConfig,
      apiBaseUrl,
      chatPublicKey,
      client,
      environment,
      reportsApiUrl,
      enablePBJ,
    ]
  );

  return (
    <EnvironmentContext.Provider value={context}>
      {children}
    </EnvironmentContext.Provider>
  );
};

const useEnvironmentContext = () => {
  const context = useContext(EnvironmentContext);
  if (!context)
    throw new Error(
      'useSessionContext must be used inside EnvironmentProvider!'
    );
  return context;
};

export { EnvironmentProvider, useEnvironmentContext };
